"use client"

import { useState, useEffect, useCallback } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import Image from 'next/image'
import Link from 'next/link'
import { 
  Home,
  Bed,
  Bath,
  Square,
  ArrowRight,
  Download,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Ruler,
  Info,
  Calculator,
  Search
} from 'lucide-react'

// Types
interface FloorPlan {
  id: string
  model?: string
  name: string
  series: string
  manufacturer: string
  category: string
  bedrooms: number
  bathrooms: number
  squareFeet: number
  dimensions?: {
    length: number
    width: number
    lengthStr: string
    widthStr: string
  }
  priority?: number
  price: {
    base?: number | null
    estimatedInstall?: number | null
    // Legacy fields for backward compatibility
    asConfigured?: number
    withInstallation?: number
  }
  images: {
    main: string
    floorplan: string
    gallery?: string[]
  }
  pdf?: string
  floorplanDescription?: string
  description?: string
  features?: string[]
  energyFeatures?: string[]
  available: boolean
  deliveryTime?: string
  tags?: string[]
  notes?: {
    disclaimer?: string
    modifications?: string
  }
}

interface FloorPlansFiltersProps {
  floorPlans: FloorPlan[]
  isHeaderHidden?: boolean
}

const categoryOptions = [
  { value: 'all', label: 'All Types' },
  { value: 'single-wide', label: 'Single Wide' },
  { value: 'double-wide', label: 'Double Wide' },
  { value: 'triple-wide', label: 'Triple Wide' }
]

const bedroomOptions = [
  { value: 'all', label: 'Any Beds' },
  { value: '1', label: '1 Bed' },
  { value: '2', label: '2 Beds' },
  { value: '3', label: '3 Beds' },
  { value: '4', label: '4 Beds' },
  { value: '5', label: '5+ Beds' }
]

const bathroomOptions = [
  { value: 'all', label: 'Any Baths' },
  { value: '1', label: '1 Bath' },
  { value: '1.5', label: '1.5 Baths' },
  { value: '2', label: '2 Baths' },
  { value: '2.5', label: '2.5 Baths' },
  { value: '3', label: '3+ Baths' }
]

const priceOptions = [
  { value: 'all', label: 'Any Price' },
  { value: '0-100000', label: 'Under $100K' },
  { value: '100000-150000', label: '$100K-$150K' },
  { value: '150000-200000', label: '$150K-$200K' },
  { value: '200000-250000', label: '$200K-$250K' },
  { value: '250000-999999', label: '$250K+' }
]

const sqftOptions = [
  { value: 'all', label: 'Any Size' },
  { value: '0-1000', label: 'Under 1,000 sqft' },
  { value: '1000-1500', label: '1,000-1,500 sqft' },
  { value: '1500-2000', label: '1,500-2,000 sqft' },
  { value: '2000-2500', label: '2,000-2,500 sqft' },
  { value: '2500-99999', label: '2,500+ sqft' }
]

export default function FloorPlansFilters({ floorPlans, isHeaderHidden = false }: FloorPlansFiltersProps) {
  const router = useRouter()
  const searchParams = useSearchParams()

  const [selectedPlan, setSelectedPlan] = useState<FloorPlan | null>(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isScrolled, setIsScrolled] = useState(false)

  // Filters - initialize from URL params
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [selectedSeries, setSelectedSeries] = useState(searchParams.get('series') || 'all')
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all')
  const [selectedBedrooms, setSelectedBedrooms] = useState(searchParams.get('bedrooms') || 'all')
  const [selectedBathrooms, setSelectedBathrooms] = useState(searchParams.get('bathrooms') || 'all')
  const [selectedPrice, setSelectedPrice] = useState(searchParams.get('price') || 'all')
  const [selectedSqft, setSelectedSqft] = useState(searchParams.get('sqft') || 'all')

  // Sync filters to URL params
  const updateUrlParams = useCallback((updates: Record<string, string>) => {
    const params = new URLSearchParams(searchParams.toString())

    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== 'all' && value !== '') {
        params.set(key, value)
      } else {
        params.delete(key)
      }
    })

    const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname
    router.replace(newUrl, { scroll: false })
  }, [searchParams, router])

  // Update URL when filters change
  useEffect(() => {
    updateUrlParams({
      search: searchQuery,
      series: selectedSeries,
      category: selectedCategory,
      bedrooms: selectedBedrooms,
      bathrooms: selectedBathrooms,
      price: selectedPrice,
      sqft: selectedSqft,
    })
  }, [searchQuery, selectedSeries, selectedCategory, selectedBedrooms, selectedBathrooms, selectedPrice, selectedSqft, updateUrlParams])

  // Handle scroll to collapse filters
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50
      setIsScrolled(scrolled)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Dynamically generate series options from the floor plans
  const uniqueSeries = Array.from(new Set(floorPlans.map(plan => plan.series))).sort()
  const seriesOptions = [
    { value: 'all', label: 'All Series' },
    ...uniqueSeries.map(series => ({ value: series, label: series }))
  ]

  // Comprehensive search function with range support
  const searchInPlan = (plan: FloorPlan, query: string): boolean => {
    if (!query) return true
    
    const searchQuery = query.toLowerCase().trim()
    
    // Convert written numbers to digits for bedroom/bathroom searches
    const numberWords: { [key: string]: string } = {
      'one': '1', 'two': '2', 'three': '3', 'four': '4', 'five': '5',
      'six': '6', 'seven': '7', 'eight': '8', 'nine': '9', 'ten': '10'
    }
    
    // Replace number words with digits in the query
    let processedQuery = searchQuery
    Object.entries(numberWords).forEach(([word, digit]) => {
      processedQuery = processedQuery.replace(new RegExp(`\\b${word}\\b`, 'g'), digit)
    })
    
    // Check for price range searches (e.g., "100-200k", "150000-200000")
    const priceRangeMatch = processedQuery.match(/(\d+)k?\s*-\s*(\d+)k?/i)
    if (priceRangeMatch) {
      let minPrice = parseFloat(priceRangeMatch[1])
      let maxPrice = parseFloat(priceRangeMatch[2])
      
      // Convert k to thousands if needed
      if (priceRangeMatch[0].includes('k')) {
        minPrice = minPrice * 1000
        maxPrice = maxPrice * 1000
      } else if (minPrice < 1000 && maxPrice < 1000) {
        // If numbers are small (like 100-200), assume they mean thousands
        minPrice = minPrice * 1000
        maxPrice = maxPrice * 1000
      }
      
      // Check if plan price is within range
      if (!plan.price?.base || plan.price.base < minPrice || plan.price.base > maxPrice) {
        return false
      }
      // Remove the price range from query for other searches
      processedQuery = processedQuery.replace(priceRangeMatch[0], '').trim()
    }
    
    // Check for square footage range searches (e.g., "600-700 sqft", "1000-1500sqft")
    // Check if the query contains sqft-related keywords first
    const hasSqftKeyword = /(?:sq\s*ft|sqft|square\s*feet|sq|sf)/i.test(processedQuery)
    const sqftRangeMatch = processedQuery.match(/(\d+)\s*-\s*(\d+)/i)
    
    if (sqftRangeMatch && hasSqftKeyword) {
      const minSqft = parseInt(sqftRangeMatch[1])
      const maxSqft = parseInt(sqftRangeMatch[2])
      
      // Check if plan sqft is within range
      if (plan.squareFeet < minSqft || plan.squareFeet > maxSqft) {
        return false
      }
      // Remove the sqft range and keyword from query for other searches
      processedQuery = processedQuery
        .replace(sqftRangeMatch[0], '')
        .replace(/(?:sq\s*ft|sqft|square\s*feet|sq|sf)/gi, '')
        .trim()
    } else if (sqftRangeMatch && !priceRangeMatch) {
      // Check if it's likely a sqft range based on the numbers (500-5000 range)
      const minSqft = parseInt(sqftRangeMatch[1])
      const maxSqft = parseInt(sqftRangeMatch[2])
      
      if (minSqft >= 500 && maxSqft <= 5000) {
        // Check if plan sqft is within range
        if (plan.squareFeet < minSqft || plan.squareFeet > maxSqft) {
          return false
        }
        // Remove the range from query for other searches
        processedQuery = processedQuery.replace(sqftRangeMatch[0], '').trim()
      }
    }
    
    // Check for specific bedroom/bathroom searches (including ranges)
    const bedroomRangeMatch = processedQuery.match(/(\d+)\s*-\s*(\d+)\s*(bedroom|bedrooms|bed|br)/i)
    const bedroomMatch = processedQuery.match(/(\d+)\s*(bedroom|bedrooms|bed|br)/i)
    const bathroomRangeMatch = processedQuery.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*(bathroom|bathrooms|bath|ba)/i)
    const bathroomMatch = processedQuery.match(/(\d+(?:\.\d+)?)\s*(bathroom|bathrooms|bath|ba)/i)
    
    // Handle bedroom range searches
    if (bedroomRangeMatch) {
      const minBedrooms = parseInt(bedroomRangeMatch[1])
      const maxBedrooms = parseInt(bedroomRangeMatch[2])
      if (plan.bedrooms < minBedrooms || plan.bedrooms > maxBedrooms) {
        return false
      }
      processedQuery = processedQuery.replace(bedroomRangeMatch[0], '').trim()
    } else if (bedroomMatch) {
      const requestedBedrooms = parseInt(bedroomMatch[1])
      if (plan.bedrooms !== requestedBedrooms) {
        return false
      }
      processedQuery = processedQuery.replace(bedroomMatch[0], '').trim()
    }
    
    // Handle bathroom range searches
    if (bathroomRangeMatch) {
      const minBathrooms = parseFloat(bathroomRangeMatch[1])
      const maxBathrooms = parseFloat(bathroomRangeMatch[2])
      if (plan.bathrooms < minBathrooms || plan.bathrooms > maxBathrooms) {
        return false
      }
      processedQuery = processedQuery.replace(bathroomRangeMatch[0], '').trim()
    } else if (bathroomMatch) {
      const requestedBathrooms = parseFloat(bathroomMatch[1])
      if (plan.bathrooms !== requestedBathrooms) {
        return false
      }
      processedQuery = processedQuery.replace(bathroomMatch[0], '').trim()
    }
    
    // Check for "under X" price searches
    const underPriceMatch = processedQuery.match(/under\s+(\d+)k?/i)
    if (underPriceMatch) {
      let maxPrice = parseFloat(underPriceMatch[1])
      // If they typed "under 70k" convert k to thousands
      if (underPriceMatch[0].includes('k')) {
        maxPrice = maxPrice * 1000
      }
      // If no price or price is over the limit, exclude
      if (!plan.price?.base || plan.price.base >= maxPrice) {
        return false
      }
      // Remove the price part from query for other searches
      processedQuery = processedQuery.replace(underPriceMatch[0], '').trim()
    }
    
    // Check for "over X" price searches
    const overPriceMatch = processedQuery.match(/over\s+(\d+)k?/i)
    if (overPriceMatch) {
      let minPrice = parseFloat(overPriceMatch[1])
      // If they typed "over 100k" convert k to thousands
      if (overPriceMatch[0].includes('k')) {
        minPrice = minPrice * 1000
      }
      // If no price or price is under the limit, exclude
      if (!plan.price?.base || plan.price.base <= minPrice) {
        return false
      }
      // Remove the price part from query for other searches
      processedQuery = processedQuery.replace(overPriceMatch[0], '').trim()
    }
    
    // If searching for specific bedrooms, filter precisely
    if (bedroomMatch) {
      const requestedBedrooms = parseInt(bedroomMatch[1])
      if (plan.bedrooms !== requestedBedrooms) {
        return false
      }
      // Remove the bedroom part from query for other searches
      processedQuery = processedQuery.replace(bedroomMatch[0], '').trim()
    }
    
    // If searching for specific bathrooms, filter precisely
    if (bathroomMatch) {
      const requestedBathrooms = parseFloat(bathroomMatch[1])
      if (plan.bathrooms !== requestedBathrooms) {
        return false
      }
      // Remove the bathroom part from query for other searches
      processedQuery = processedQuery.replace(bathroomMatch[0], '').trim()
    }
    
    // If no remaining query after bedroom/bathroom filtering, we have a match
    if (!processedQuery) return true
    
    // Create searchable text from all relevant fields
    const searchableText = [
      plan.id,
      plan.id.toLowerCase(),
      plan.id.toUpperCase(),
      plan.name,
      plan.model,
      plan.series,
      plan.manufacturer,
      plan.category,
      plan.floorplanDescription,
      plan.description,
      plan.deliveryTime,
      ...(plan.tags || []),
      ...(plan.features || []),
      ...(plan.energyFeatures || []),
      `${plan.squareFeet} sqft`,
      `${plan.squareFeet} sq ft`,
      `${plan.squareFeet} square feet`,
      `${plan.squareFeet}sqft`,
      // Add pricing to searchable fields
      plan.price?.base ? `${plan.price.base}` : '',
      plan.price?.base ? `$${plan.price.base}` : '',
      plan.price?.base ? `${Math.round(plan.price.base)}` : '',
      plan.price?.estimatedInstall ? `${plan.price.estimatedInstall}` : '',
      plan.price?.estimatedInstall ? `$${plan.price.estimatedInstall}` : '',
      plan.dimensions ? `${plan.dimensions.lengthStr} x ${plan.dimensions.widthStr}` : '',
      plan.dimensions ? `${plan.dimensions.length}x${plan.dimensions.width}` : '',
      plan.dimensions ? `${plan.dimensions.width}x${plan.dimensions.length}` : '',
      plan.notes?.disclaimer || '',
      plan.notes?.modifications || ''
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    
    // Split remaining query into terms and check if all are present
    const searchTerms = processedQuery.split(/\s+/).filter(term => term.length > 0)
    
    // For each search term, check if it appears in the searchable text
    return searchTerms.every(term => {
      // For very short terms (1-2 chars), require word boundary match to avoid false positives
      if (term.length <= 2) {
        const regex = new RegExp(`\\b${term}\\b`, 'i')
        return regex.test(searchableText)
      }
      // For longer terms, use includes for partial matching
      return searchableText.includes(term)
    })
  }

  // Filter and sort floor plans
  const filteredPlans = floorPlans
    .filter(plan => {
      // Search filter
      if (!searchInPlan(plan, searchQuery)) return false
      
      // Series filter
      if (selectedSeries !== 'all' && plan.series !== selectedSeries) return false
      
      // Category filter
      if (selectedCategory !== 'all' && plan.category !== selectedCategory) return false
      
      // Bedrooms filter
      if (selectedBedrooms !== 'all') {
        const beds = parseInt(selectedBedrooms)
        if (beds === 5 && plan.bedrooms < 5) return false
        if (beds < 5 && plan.bedrooms !== beds) return false
      }
      
      // Bathrooms filter
      if (selectedBathrooms !== 'all') {
        const baths = parseFloat(selectedBathrooms)
        if (baths === 3 && plan.bathrooms < 3) return false
        if (baths < 3 && plan.bathrooms !== baths) return false
      }
      
      // Price filter - work with base price
      if (selectedPrice !== 'all') {
        const price = plan.price?.base || plan.price?.estimatedInstall || plan.price?.withInstallation
        if (price) {
          const [min, max] = selectedPrice.split('-').map(Number)
          if (price < min || price > max) return false
        } else {
          // If no price is available, exclude from filtered results when price filter is active
          return false
        }
      }
      
      // Square feet filter
      if (selectedSqft !== 'all') {
        const [min, max] = selectedSqft.split('-').map(Number)
        if (plan.squareFeet < min || plan.squareFeet > max) return false
      }
      
      return true
    })
    .sort((a, b) => {
      // Best Sellers (priority 1) are always pinned to the top — in the
      // general/unfiltered view, or within a series when filtering by series
      // (this comparator runs after filtering, so it applies to whichever set
      // is showing).
      const aBestSeller = a.priority === 1 ? 0 : 1
      const bBestSeller = b.priority === 1 ? 0 : 1
      if (aBestSeller !== bBestSeller) return aBestSeller - bBestSeller

      // Otherwise sort by size, biggest to smallest.
      const sqftDiff = (b.squareFeet ?? 0) - (a.squareFeet ?? 0)
      if (sqftDiff !== 0) return sqftDiff

      // Stable tiebreak for equal square footage.
      return a.name.localeCompare(b.name)
    })

  // Format currency
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price)
  }

  // Handle image navigation in modal
  const getAllImages = (plan: FloorPlan) => {
    const images = [plan.images.main]
    // Only add floorplan if it's different from main image
    if (plan.images.floorplan && plan.images.floorplan !== plan.images.main) {
      images.push(plan.images.floorplan)
    }
    if (plan.images.gallery) {
      // Also filter out any gallery images that are duplicates
      const uniqueGalleryImages = plan.images.gallery.filter(img => !images.includes(img))
      images.push(...uniqueGalleryImages)
    }
    return images
  }

  const nextImage = () => {
    if (!selectedPlan) return
    const images = getAllImages(selectedPlan)
    setCurrentImageIndex((prev) => (prev + 1) % images.length)
  }

  const prevImage = () => {
    if (!selectedPlan) return
    const images = getAllImages(selectedPlan)
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
  }

  return (
    <>
      {/* Filters Section */}
      <section className={`bg-white sticky z-30 shadow-sm transition-all duration-300 ${isHeaderHidden ? 'top-0' : 'top-[70px]'}`}>
        <div className={`container mx-auto px-4 transition-all duration-300 ${isScrolled ? 'py-1' : 'py-3'}`}>
          {!isScrolled && (
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span className="text-xs font-medium text-gray-700">Filter by:</span>
            </div>
          )}
          
          {/* Search Bar */}
          <div className={`col-span-2 md:col-span-3 lg:col-span-6 mb-2 ${isScrolled ? 'mb-1' : ''}`}>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 ${isScrolled ? 'w-3 h-3' : 'w-4 h-4'}`} />
              <input
                type="text"
                placeholder="Search floor plans by name, features, size, or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full bg-white border border-gray-200 rounded-lg font-medium text-gray-700 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c88546] focus:border-transparent transition-all ${
                  isScrolled ? 'pl-7 pr-3 py-1 text-[10px] rounded-md' : 'pl-9 pr-4 py-2 text-sm'
                }`}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors ${isScrolled ? 'p-0.5' : 'p-1'}`}
                >
                  <X className={isScrolled ? 'w-3 h-3' : 'w-4 h-4'} />
                </button>
              )}
            </div>
          </div>
          
          <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 ${isScrolled ? 'gap-1.5' : 'gap-3'}`}>
            <div className="relative">
              <select
                value={selectedSeries}
                onChange={(e) => setSelectedSeries(e.target.value)}
                className={`appearance-none w-full bg-gray-50 rounded-lg font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#c88546] focus:bg-white transition-all cursor-pointer ${
                  isScrolled ? 'px-2 py-1 text-[10px] rounded-md' : 'px-3 py-1.5 text-xs'
                }`}
              >
                {seriesOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className={`absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none ${isScrolled ? 'w-3 h-3' : 'w-4 h-4'}`} />
            </div>

            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className={`appearance-none w-full bg-gray-50 rounded-lg font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#c88546] focus:bg-white transition-all cursor-pointer ${
                  isScrolled ? 'px-2 py-1 text-[10px] rounded-md' : 'px-3 py-1.5 text-xs'
                }`}
              >
                {categoryOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className={`absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none ${isScrolled ? 'w-3 h-3' : 'w-4 h-4'}`} />
            </div>

            <div className="relative">
              <select
                value={selectedBedrooms}
                onChange={(e) => setSelectedBedrooms(e.target.value)}
                className={`appearance-none w-full bg-gray-50 rounded-lg font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#c88546] focus:bg-white transition-all cursor-pointer ${
                  isScrolled ? 'px-2 py-1 text-[10px] rounded-md' : 'px-3 py-1.5 text-xs'
                }`}
              >
                {bedroomOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className={`absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none ${isScrolled ? 'w-3 h-3' : 'w-4 h-4'}`} />
            </div>

            <div className="relative">
              <select
                value={selectedBathrooms}
                onChange={(e) => setSelectedBathrooms(e.target.value)}
                className={`appearance-none w-full bg-gray-50 rounded-lg font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#c88546] focus:bg-white transition-all cursor-pointer ${
                  isScrolled ? 'px-2 py-1 text-[10px] rounded-md' : 'px-3 py-1.5 text-xs'
                }`}
              >
                {bathroomOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className={`absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none ${isScrolled ? 'w-3 h-3' : 'w-4 h-4'}`} />
            </div>

            <div className="relative">
              <select
                value={selectedPrice}
                onChange={(e) => setSelectedPrice(e.target.value)}
                className={`appearance-none w-full bg-gray-50 rounded-lg font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#c88546] focus:bg-white transition-all cursor-pointer ${
                  isScrolled ? 'px-2 py-1 text-[10px] rounded-md' : 'px-3 py-1.5 text-xs'
                }`}
              >
                {priceOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className={`absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none ${isScrolled ? 'w-3 h-3' : 'w-4 h-4'}`} />
            </div>

            <div className="relative">
              <select
                value={selectedSqft}
                onChange={(e) => setSelectedSqft(e.target.value)}
                className={`appearance-none w-full bg-gray-50 rounded-lg font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#c88546] focus:bg-white transition-all cursor-pointer ${
                  isScrolled ? 'px-2 py-1 text-[10px] rounded-md' : 'px-3 py-1.5 text-xs'
                }`}
              >
                {sqftOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className={`absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none ${isScrolled ? 'w-3 h-3' : 'w-4 h-4'}`} />
            </div>
          </div>
          
          <div className={`flex items-center justify-between ${isScrolled ? 'mt-1 pt-1 border-t' : 'mt-3 pt-2 border-t'}`}>
            <p className={`font-medium text-gray-900 ${isScrolled ? 'text-[10px]' : 'text-xs'}`}>
              {filteredPlans.length} {filteredPlans.length === 1 ? 'home' : 'homes'} available
            </p>
            {(searchQuery || selectedSeries !== 'all' || selectedCategory !== 'all' || selectedBedrooms !== 'all' || 
              selectedBathrooms !== 'all' || selectedPrice !== 'all' || selectedSqft !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('')
                  setSelectedSeries('all')
                  setSelectedCategory('all')
                  setSelectedBedrooms('all')
                  setSelectedBathrooms('all')
                  setSelectedPrice('all')
                  setSelectedSqft('all')
                }}
                className={`flex items-center gap-1 font-medium text-gray-600 hover:text-[#c88546] transition-colors ${isScrolled ? 'text-[10px]' : 'text-xs'}`}
              >
                <X className={isScrolled ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
                Clear filters
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Floor Plans Grid */}
      <section className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPlans.map((plan, index) => (
            <Link
              key={plan.id}
              href={`/floor-plans/${plan.id}`}
              className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 relative block"
            >
              {/* Best Seller Tag - Only show for priority 1 */}
              {plan.priority === 1 && (
                <div className="absolute top-4 left-4 z-10">
                  <span className="bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md">
                    BEST SELLER
                  </span>
                </div>
              )}

              {/* Image */}
              <div className="relative h-64 bg-white p-4 rounded-t-2xl overflow-hidden">
                <Image
                  src={plan.images.main}
                  alt={plan.name}
                  fill
                  className="object-contain"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  priority={index < 6}
                  loading={index < 6 ? "eager" : "lazy"}
                />
              </div>

              {/* Content */}
              <div className="p-6 space-y-4 rounded-b-2xl">
                {/* Header */}
                <div>
                  <p className="text-sm font-semibold text-[#c88546] uppercase tracking-wider mb-1">
                    {plan.series}{plan.model && ` • ${plan.model}`}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#c88546] transition-colors">
                    {plan.name}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    by {plan.manufacturer}
                  </p>
                </div>

                {/* Stats Grid */}
                <div className="flex items-center gap-4 py-3 px-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-1.5 text-sm">
                    <Bed className="w-4 h-4 text-[#c88546]" />
                    <span className="font-medium text-gray-700">{plan.bedrooms}</span>
                  </div>
                  <div className="w-px h-4 bg-gray-300" />
                  <div className="flex items-center gap-1.5 text-sm">
                    <Bath className="w-4 h-4 text-[#c88546]" />
                    <span className="font-medium text-gray-700">{plan.bathrooms}</span>
                  </div>
                  <div className="w-px h-4 bg-gray-300" />
                  <div className="flex items-center gap-1.5 text-sm">
                    <Square className="w-4 h-4 text-[#c88546]" />
                    <span className="font-medium text-gray-700">{plan.squareFeet.toLocaleString()}</span>
                  </div>
                </div>

                {/* Dimensions */}
                {plan.dimensions && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Ruler className="w-4 h-4 text-gray-400" />
                    <span>Dimensions: <span className="font-medium text-gray-900">{plan.dimensions.widthStr} × {plan.dimensions.lengthStr}</span></span>
                  </div>
                )}

                {/* Price */}
                <div className="border-t border-gray-100 pt-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Pricing</p>
                      <div className="group/tooltip relative">
                        <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-200 z-50 shadow-lg">
                          <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                          This is the base price for the home. Final pricing will vary based on site conditions, installation costs, permits, and any customizations you choose.
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1 mb-3">
                      {plan.price.base && plan.price.base !== null && (
                        <div className="flex justify-between items-baseline">
                          <span className="text-sm text-gray-600">Base Home Price:</span>
                          <span className="text-lg font-semibold text-gray-900">{formatPrice(plan.price.base)}</span>
                        </div>
                      )}
                      {(plan.price.estimatedInstall !== null || plan.price.withInstallation) ? (
                        <div className="flex justify-between items-baseline">
                          <span className="text-sm text-gray-600">Est. Installed:</span>
                          <span className="text-2xl font-bold text-[#c88546]">
                            {formatPrice(plan.price.estimatedInstall || plan.price.withInstallation || 0)}
                          </span>
                        </div>
                      ) : (
                        <div className="text-center py-2">
                          <p className="text-sm text-gray-500">*plus delivery, tax and installation</p>
                        </div>
                      )}
                    </div>
                    <Link 
                      href={`/get-started?model=${encodeURIComponent(plan.name)}&id=${encodeURIComponent(plan.id)}`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#c88546] group-hover:text-[#b87436] transition-colors"
                    >
                      <Calculator className="w-4 h-4" />
                      Contact for final installed pricing
                    </Link>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600 line-clamp-2">
                  {plan.floorplanDescription || plan.description}
                </p>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <div className="flex-1 bg-gray-900 text-white px-4 py-3 rounded-xl group-hover:bg-[#c88546] transition-all duration-300 flex items-center justify-center gap-2 font-medium">
                    View Details
                    <ArrowRight className="w-4 h-4" />
                  </div>
                  {plan.pdf && (
                    <div className="px-4 py-3 bg-gray-50 group-hover:bg-gray-100 rounded-xl transition-colors flex items-center justify-center">
                      <Download className="w-5 h-5 text-gray-700" />
                    </div>
                  )}
                </div>

                {/* Delivery */}
                {plan.deliveryTime && (
                  <p className="text-xs text-center text-gray-500 font-medium">
                    Est. Delivery: {plan.deliveryTime}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>

        {/* No results */}
        {filteredPlans.length === 0 && (
          <div className="text-center py-12">
            <Home className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No floor plans found
            </h3>
            <p className="text-gray-600 mb-4">
              Try adjusting your filters to see more options
            </p>
            <button
              onClick={() => {
                setSearchQuery('')
                setSelectedSeries('all')
                setSelectedCategory('all')
                setSelectedBedrooms('all')
                setSelectedBathrooms('all')
                setSelectedPrice('all')
                setSelectedSqft('all')
              }}
              className="bg-[#c88546] text-white px-6 py-2 rounded hover:bg-[#b87436] transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </section>

      {/* Detail Modal */}
      {selectedPlan && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div 
              className="fixed inset-0 bg-black/40 backdrop-blur-md"
              onClick={() => setSelectedPlan(null)}
            />
            
            <div className="relative bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedPlan.name}</h2>
                  <p className="text-gray-600">{selectedPlan.series}{selectedPlan.model && ` • Model: ${selectedPlan.model}`}</p>
                  <p className="text-sm text-gray-500">by {selectedPlan.manufacturer}</p>
                </div>
                <button
                  onClick={() => setSelectedPlan(null)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="grid lg:grid-cols-2 gap-6 p-6">
                {/* Image Gallery */}
                <div>
                  <div className="relative aspect-[4/3] bg-white mb-4 p-6">
                    <Image
                      src={getAllImages(selectedPlan)[currentImageIndex]}
                      alt={`${selectedPlan.name} - Image ${currentImageIndex + 1}`}
                      fill
                      className="object-contain"
                      sizes="(max-width: 1200px) 100vw, 50vw"
                      priority
                    />
                    
                    {getAllImages(selectedPlan).length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full hover:bg-white"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full hover:bg-white"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </>
                    )}
                  </div>

                  {/* Thumbnail Navigation */}
                  {getAllImages(selectedPlan).length > 1 && (
                    <div className="grid grid-cols-4 gap-2">
                      {getAllImages(selectedPlan).map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentImageIndex(idx)}
                          className={`relative aspect-square rounded overflow-hidden border-2 bg-white p-1 ${
                            idx === currentImageIndex ? 'border-[#c88546]' : 'border-gray-200'
                          }`}
                        >
                          <Image
                            src={img}
                            alt={`Thumbnail ${idx + 1}`}
                            fill
                            className="object-contain p-1"
                            sizes="100px"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Details */}
                <div>
                  {/* Pricing */}
                  <div className="bg-gray-50 rounded-lg p-6 mb-6">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="font-bold text-lg">Pricing</h3>
                      <div className="group/tooltip relative">
                        <Info className="w-5 h-5 text-gray-400 cursor-help" />
                        <div className="absolute top-full right-0 mt-2 w-72 p-4 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-200 z-50 shadow-lg">
                          <div className="absolute top-[-4px] right-4 w-2 h-2 bg-gray-900 rotate-45"></div>
                          <p className="font-semibold mb-2">Base Pricing Information</p>
                          <p className="text-xs mb-2">This is the base price for the home model.</p>
                          <p className="text-xs">Final pricing will vary based on:</p>
                          <ul className="text-xs mt-1 space-y-0.5">
                            <li>• Site preparation & grading</li>
                            <li>• Foundation type</li>
                            <li>• Utility connection distances</li>
                            <li>• Local permits & fees</li>
                            <li>• Custom options selected</li>
                          </ul>
                          <p className="text-xs mt-2 font-medium">Schedule a site evaluation for exact pricing.</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {selectedPlan.price.base && selectedPlan.price.base !== null && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Base Home Price:</span>
                          <span className="text-xl font-semibold">{formatPrice(selectedPlan.price.base)}</span>
                        </div>
                      )}
                      {(selectedPlan.price.estimatedInstall !== null || selectedPlan.price.withInstallation) ? (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Estimated Installed:</span>
                          <span className="font-bold text-2xl text-[#c88546]">
                            {formatPrice(selectedPlan.price.estimatedInstall || selectedPlan.price.withInstallation || 0)}
                          </span>
                        </div>
                      ) : (
                        <div className="text-center py-4 bg-gray-50 rounded">
                          <p className="text-gray-600 font-medium">*plus delivery, tax and installation</p>
                          <p className="text-sm text-gray-500 mt-1">Call (928) 361-0298</p>
                        </div>
                      )}
                      <div className="pt-3 border-t">
                        <Link
                          href={`/get-started?model=${encodeURIComponent(selectedPlan.name)}&id=${encodeURIComponent(selectedPlan.id)}`}
                          className="w-full bg-[#c88546] text-white px-4 py-3 rounded-lg hover:bg-[#b87436] transition-colors flex items-center justify-center gap-2 font-medium"
                        >
                          <Calculator className="w-5 h-5" />
                          Contact for Final Installed Pricing
                        </Link>
                        <p className="text-xs text-gray-500 text-center mt-2">
                          Professional site evaluation • No obligation quote
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Specifications */}
                  <div className="mb-6">
                    <h3 className="font-bold text-lg mb-3">Specifications</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-gray-600">Bedrooms:</span>
                        <span className="ml-2 font-semibold">{selectedPlan.bedrooms}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Bathrooms:</span>
                        <span className="ml-2 font-semibold">{selectedPlan.bathrooms}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Square Feet:</span>
                        <span className="ml-2 font-semibold">{selectedPlan.squareFeet.toLocaleString()}</span>
                      </div>
                      {selectedPlan.dimensions && (
                        <div>
                          <span className="text-gray-600">Dimensions:</span>
                          <span className="ml-2 font-semibold">{selectedPlan.dimensions.widthStr} × {selectedPlan.dimensions.lengthStr}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mb-6">
                    <h3 className="font-bold text-lg mb-3">Description</h3>
                    <p className="text-gray-600">{selectedPlan.floorplanDescription || selectedPlan.description}</p>
                  </div>

                  {/* Features */}
                  {selectedPlan.features && selectedPlan.features.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-bold text-lg mb-3">Standard Features</h3>
                      <ul className="grid grid-cols-2 gap-2">
                        {selectedPlan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm">
                            <span className="w-2 h-2 bg-[#c88546] rounded-full mr-2" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Energy Features */}
                  {selectedPlan.energyFeatures && selectedPlan.energyFeatures.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-bold text-lg mb-3">Energy Features</h3>
                      <ul className="grid grid-cols-2 gap-2">
                        {selectedPlan.energyFeatures.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-2" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setSelectedPlan(null)
                        window.dispatchEvent(new CustomEvent('showQuoteModal'))
                      }}
                      className="flex-1 bg-gray-900 text-white px-6 py-3 rounded-lg text-center hover:bg-[#c88546] transition-colors font-medium"
                    >
                      Get Free Buyer's Guide
                    </button>
                    {selectedPlan.pdf && (
                      <a
                        href={selectedPlan.pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-6 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors flex items-center"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        PDF
                      </a>
                    )}
                  </div>

                  {/* Delivery Time */}
                  {selectedPlan.deliveryTime && (
                    <p className="text-sm text-gray-600 mt-4">
                      Estimated delivery: {selectedPlan.deliveryTime}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}